import styled, { css, keyframes } from "styled-components";

const colors = {
  background: "#0d0a0b",
  foreground: "#f0ece8",
  card: "#16100f",
  primary: "#833c41",
  primaryForeground: "#f9f3ef",
  secondary: "#1f1615",
  muted: "#7a6560",
  accent: "#9e4a50",
  border: "rgba(131, 60, 65, 0.18)",
  success: "#4ade80",
};

const nodePulse = keyframes`
  0%, 100% {
    opacity: 0.3;
  }

  50% {
    opacity: 1;
  }
`;

export const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const Container = styled.section`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${colors.background};
  color: ${colors.foreground};
  font-family: Inter, "Helvetica Neue", Arial, sans-serif;
`;

export const BrandPanel = styled.aside`
  position: relative;
  display: flex;
  width: 58%;
  min-height: 100vh;
  overflow: hidden;
  flex-direction: column;

  @media (max-width: 1024px) {
    display: none;
  }
`;

export const BrandImage = styled.div`
  position: absolute;
  inset: 0;
  background-image: url("https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&h=900&fit=crop&auto=format");
  background-position: center;
  background-size: cover;
`;

export const BrandOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(13, 10, 11, 0.94) 0%,
    rgba(13, 10, 11, 0.72) 52%,
    rgba(131, 60, 65, 0.34) 100%
  );
`;

export const NetworkSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0.3;
`;

export const PulseNode = styled.circle<{ delay: string }>`
  animation: ${nodePulse} 3s ease-in-out ${({ delay }) => delay} infinite;
`;

export const BrandContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  height: 100%;
  min-height: 100vh;
  flex-direction: column;
  padding: 40px 56px;

  @media (min-width: 1440px) {
    padding: 56px;
  }
`;

export const BrandLockup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const LogoMark = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 6px;
  background: ${colors.primary};
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.32);

  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
`;

export const Eyebrow = styled.span`
  color: ${colors.muted};
  font-family: "DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 10px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

export const HeroCopy = styled.div`
  display: flex;
  max-width: 440px;
  flex: 1;
  flex-direction: column;
  justify-content: center;
`;

export const AccentLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  color: ${colors.accent};
  font-family: "DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

export const HeroTitle = styled.h1`
  margin: 0 0 24px;
  color: ${colors.foreground};
  font-family: "Cormorant Infant", Georgia, "Times New Roman", serif;
  font-size: clamp(2.75rem, 4.5vw, 3.85rem);
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.08;

  em {
    color: ${colors.accent};
    font-style: italic;
  }
`;

export const HeroText = styled.p`
  max-width: 390px;
  margin: 0;
  color: ${colors.muted};
  font-size: 15px;
  line-height: 1.75;
`;

export const MetricsPanel = styled.div`
  border-top: 1px solid ${colors.border};
  padding-top: 24px;
`;

export const LiveStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  color: ${colors.muted};
  font-family: "DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;

  &::before {
    width: 6px;
    height: 6px;
    content: "";
    border-radius: 50%;
    background: ${colors.success};
    box-shadow: 0 0 8px ${colors.success};
  }
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
`;

export const MetricValue = styled.div`
  color: ${colors.foreground};
  font-family: "Cormorant Infant", Georgia, "Times New Roman", serif;
  font-size: 22px;
  font-weight: 600;
  line-height: 1;
`;

export const MetricMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  color: ${colors.muted};
  font-size: 11px;

  strong {
    color: ${colors.success};
    font-family: "DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
    font-size: 10px;
    font-weight: 500;
  }
`;

export const FormPanel = styled.main`
  position: relative;
  display: flex;
  min-height: 100vh;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 48px;
  background: ${colors.card};

  @media (max-width: 640px) {
    align-items: flex-start;
    padding: 40px 24px;
  }
`;

export const FormShell = styled.div`
  width: 100%;
  max-width: 380px;
`;

export const MobileBrand = styled(BrandLockup)`
  display: none;
  margin-bottom: 48px;

  @media (max-width: 1024px) {
    display: flex;
  }
`;

export const FeedbackWrapper = styled.div`
  position: fixed;
  top: 24px;
  left: 50%;
  z-index: 10;
  width: min(420px, calc(100% - 32px));
  transform: translateX(-50%);
`;

export const FormHeader = styled.div`
  margin-bottom: 40px;
`;

export const FormTitle = styled.h2`
  margin: 8px 0 4px;
  color: ${colors.foreground};
  font-family: "Cormorant Infant", Georgia, "Times New Roman", serif;
  font-size: 2rem;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.2;
`;

export const FormSubtitle = styled.p`
  margin: 0;
  color: ${colors.muted};
  font-size: 14px;
`;

export const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const FormContentWrapper = styled.div`
  width: min(420px, calc(100vw - 32px));
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 36px 32px;
  background: ${colors.card};
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.34);

  h4 {
    color: ${colors.foreground};
    font-family: "Cormorant Infant", Georgia, "Times New Roman", serif;
    font-weight: 500;
  }

  p {
    color: ${colors.muted} !important;
    text-align: center;
  }
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FieldRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const Label = styled.label`
  color: ${colors.muted};
  font-family: "DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const InputField = styled.input`
  width: 100%;
  height: 42px;
  border: 1px solid ${colors.border};
  border-radius: 6px;
  outline: none;
  background: ${colors.secondary};
  color: ${colors.foreground};
  font-size: 14px;
  line-height: 1.5;
  padding: 10px 42px 10px 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: rgba(122, 101, 96, 0.82);
  }

  &:focus {
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px rgba(131, 60, 65, 0.18);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

export const Icon = styled.div<{ button?: boolean }>`
  position: absolute;
  top: 50%;
  right: 13px;
  display: flex;
  color: ${colors.muted};
  transform: translateY(-50%);

  ${({ button }) =>
    !button &&
    css`
      pointer-events: none;
    `}
`;

export const IconButton = styled.button`
  display: flex;
  border: none;
  padding: 0;
  color: ${colors.muted};
  background: transparent;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

export const TextLink = styled.a`
  border: none;
  padding: 0;
  color: ${colors.accent};
  background: transparent;
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: ${colors.primaryForeground};
  }
`;

export const RememberRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${colors.muted};
  cursor: pointer;
  font-size: 13px;
  user-select: none;
`;

export const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: ${colors.primary};
  cursor: pointer;
`;

export const SubmitButton = styled.button`
  width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 6px;
  margin-top: 4px;
  padding: 11px 20px;
  background: ${colors.primary};
  color: ${colors.primaryForeground};
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    background: ${colors.accent};
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    background: ${colors.secondary};
  }
`;

export const SecureNote = styled.div`
  position: absolute;
  right: 24px;
  bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${colors.muted};
  font-family: "DM Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;

  &::before {
    width: 6px;
    height: 6px;
    content: "";
    border-radius: 50%;
    background: ${colors.primary};
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

export const Line = styled.div`
  width: 100%;
  height: 1px;
  margin-top: 4px;
  background: ${colors.border};
`;

export const LinksWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 4px;

  a {
    color: ${colors.accent};
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    transition: color 0.2s ease, transform 0.2s ease;

    &:hover {
      color: ${colors.primaryForeground};
      transform: translateY(-1px);
    }
  }
`;
